/**
 * WhoopCompareView - Dedicated comparison view for WHOOP vs App metrics
 * 
 * Full feature set for ALL users (Standard and Premium)
 * - Metric selectors (WHOOP + App metrics)
 * - Date range filters (7, 14, 30, 90 days)
 * - Overlaid time-series chart
 * - Scatter plot
 * - Correlation summary from correlation_insights_v2 (if exists)
 * 
 * UI-only, no AI reasoning, no new calculations
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter } from 'recharts';
import { TrendingUp, Activity, Brain, Heart, Moon, Flame, Zap, Calendar, Info, Sparkles, Smile, Utensils, CircleDot, AlertCircle } from 'lucide-react';
import { useWhoopData } from '@/hooks/useWhoopData';
import { useStoredCorrelationSummaries, formatMetricNameForDisplay, getCorrelationCategoryLabel } from '@/hooks/useStoredCorrelationSummaries';
import { useILScore } from '@/hooks/useILScore';
import { useMentalWellnessScore } from '@/hooks/useMentalWellnessScore';
import { useDailyCheckinMetrics, getCyclePhaseFromDay, calculateFoodQualityScore } from '@/hooks/useDailyCheckinMetrics';
import { Skeleton } from '@/components/ui/skeleton';
import { format, subDays, parseISO } from 'date-fns';

type DateRange = 7 | 14 | 30 | 90;

interface WhoopMetric {
  id: string;
  label: string;
  icon: any;
  dataKey: string;
  color: string;
}

interface AppMetric {
  id: string;
  label: string;
  icon: any;
  dataKey: string;
  source: 'checkin' | 'il' | 'mental' | 'daily';
  color: string;
}

const WHOOP_METRICS: WhoopMetric[] = [
  { id: 'recovery', label: 'Recovery Score', icon: Zap, dataKey: 'recovery_score', color: 'hsl(var(--accent))' },
  { id: 'hrv', label: 'HRV', icon: Heart, dataKey: 'hrv_score', color: 'hsl(var(--primary))' },
  { id: 'sleep', label: 'Sleep Performance', icon: Moon, dataKey: 'sleep_performance', color: 'hsl(177 41% 76%)' },
  { id: 'strain', label: 'Strain', icon: Activity, dataKey: 'strain_score', color: 'hsl(42 34% 73%)' },
  { id: 'rhr', label: 'Resting Heart Rate', icon: Heart, dataKey: 'resting_heart_rate', color: 'hsl(var(--secondary))' },
];

const APP_METRICS: AppMetric[] = [
  { id: 'mood', label: 'Mood', icon: Brain, dataKey: 'mood_score', source: 'mental', color: 'hsl(var(--chart-1))' },
  { id: 'energy', label: 'Energy', icon: Zap, dataKey: 'energy_score', source: 'mental', color: 'hsl(var(--chart-2))' },
  { id: 'stress', label: 'Stress Level', icon: Brain, dataKey: 'stress_score', source: 'il', color: 'hsl(var(--chart-3))' },
  { id: 'il_score', label: 'Inflammation Score', icon: Flame, dataKey: 'il_score', source: 'il', color: 'hsl(var(--chart-4))' },
  { id: 'sleep_quality', label: 'Sleep Quality', icon: Moon, dataKey: 'sleep_score', source: 'il', color: 'hsl(var(--chart-5))' },
  { id: 'mental_wellness', label: 'Mental Wellness', icon: Brain, dataKey: 'overall_score', source: 'mental', color: 'hsl(var(--primary))' },
  { id: 'emotion', label: 'Emotion', icon: Smile, dataKey: 'emotion_score', source: 'daily', color: 'hsl(var(--accent))' },
  { id: 'food_quality', label: 'Food Quality', icon: Utensils, dataKey: 'food_quality_score', source: 'daily', color: 'hsl(42 34% 73%)' },
  { id: 'cycle_phase', label: 'Cycle Phase', icon: CircleDot, dataKey: 'cycle_phase', source: 'daily', color: 'hsl(177 41% 76%)' },
  { id: 'cycle_symptoms', label: 'Cycle Symptoms', icon: AlertCircle, dataKey: 'cycle_symptom_count', source: 'daily', color: 'hsl(var(--secondary))' },
];

const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
];

export function WhoopCompareView() {
  const [selectedWhoopMetric, setSelectedWhoopMetric] = useState<string>('recovery');
  const [selectedAppMetric, setSelectedAppMetric] = useState<string>('mood');
  const [dateRange, setDateRange] = useState<DateRange>(30);

  const { data: whoopData, isLoading: whoopLoading, isConnected } = useWhoopData(dateRange);
  const { data: ilData, isLoading: ilLoading } = useILScore(dateRange);
  const { data: mentalData, isLoading: mentalLoading } = useMentalWellnessScore(dateRange);
  const { data: dailyCheckinData, isLoading: dailyLoading } = useDailyCheckinMetrics(dateRange);
  const { data: correlationSummaries, isLoading: summariesLoading } = useStoredCorrelationSummaries(dateRange);

  const isLoading = whoopLoading || ilLoading || mentalLoading || dailyLoading;

  const whoopMetricConfig = WHOOP_METRICS.find(m => m.id === selectedWhoopMetric);
  const appMetricConfig = APP_METRICS.find(m => m.id === selectedAppMetric);

  // Map emotion strings to numeric scores for charting
  const emotionToScore = (emotion: string | null): number | null => {
    if (!emotion) return null;
    const emotionMap: Record<string, number> = {
      'happy': 90, 'joyful': 95, 'content': 80, 'peaceful': 75, 'calm': 70,
      'neutral': 50, 'okay': 50,
      'anxious': 35, 'stressed': 30, 'sad': 25, 'angry': 20, 'frustrated': 30,
      'depressed': 15, 'overwhelmed': 20
    };
    return emotionMap[emotion.toLowerCase()] ?? 50;
  };

  // Combine data by date
  const combinedData = useMemo(() => {
    const dataMap = new Map<string, any>();

    whoopData?.forEach(item => {
      const existing = dataMap.get(item.date) || { date: item.date };
      dataMap.set(item.date, { 
        ...existing, 
        recovery_score: item.recovery_score,
        hrv_score: item.hrv_score,
        sleep_performance: item.sleep_performance,
        strain_score: item.strain_score,
        resting_heart_rate: item.resting_heart_rate,
      });
    });

    ilData?.forEach((item: any) => {
      const existing = dataMap.get(item.date) || { date: item.date };
      dataMap.set(item.date, { 
        ...existing, 
        il_score: item.il_score,
        stress_score: item.stress_score,
        sleep_score: item.sleep_score,
      });
    });

    mentalData?.forEach((item: any) => {
      const existing = dataMap.get(item.date) || { date: item.date };
      dataMap.set(item.date, { 
        ...existing, 
        mood_score: item.mood_score,
        energy_score: item.energy_score,
        overall_score: item.overall_score,
      });
    });

    // Add daily check-in metrics (emotion, food quality, cycle)
    dailyCheckinData?.forEach((item: any) => {
      const existing = dataMap.get(item.date) || { date: item.date };
      dataMap.set(item.date, {
        ...existing,
        emotion_score: emotionToScore(item.emotion),
        food_quality_score: calculateFoodQualityScore(item.processed_food_level, item.healthy_food_level),
        cycle_phase: getCyclePhaseFromDay(item.menstrual_cycle_day),
        cycle_symptom_count: item.menstrual_symptoms?.length ?? null,
      });
    });

    return Array.from(dataMap.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(d => ({
        ...d,
        displayDate: format(parseISO(d.date), 'MMM d'),
      }));
  }, [whoopData, ilData, mentalData, dailyCheckinData]);

  // Find relevant correlation summary
  const relevantCorrelation = useMemo(() => {
    if (!correlationSummaries || !whoopMetricConfig || !appMetricConfig) return null;
    
    return correlationSummaries.find(s => 
      (s.metric_a.includes(selectedWhoopMetric) && s.metric_b.includes(selectedAppMetric)) ||
      (s.metric_b.includes(selectedWhoopMetric) && s.metric_a.includes(selectedAppMetric))
    );
  }, [correlationSummaries, selectedWhoopMetric, selectedAppMetric, whoopMetricConfig, appMetricConfig]);

  // Prepare scatter data
  const scatterData = useMemo(() => {
    if (!whoopMetricConfig || !appMetricConfig) return [];
    
    return combinedData
      .filter(d => d[whoopMetricConfig.dataKey] != null && d[appMetricConfig.dataKey] != null)
      .map(d => ({
        x: d[whoopMetricConfig.dataKey],
        y: d[appMetricConfig.dataKey],
        date: d.displayDate,
      }));
  }, [combinedData, whoopMetricConfig, appMetricConfig]);

  if (!isConnected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            WHOOP Comparison
          </CardTitle>
          <CardDescription>
            Connect your WHOOP device to compare biometric data with your app metrics
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metric Selectors */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-primary" />
            Compare WHOOP with App Data
          </CardTitle>
          <CardDescription>
            Select metrics to visualise relationships between your biometric data and daily tracking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* WHOOP Metric Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Activity className="h-4 w-4 text-primary" />
                WHOOP Metric
              </label>
              <Select value={selectedWhoopMetric} onValueChange={setSelectedWhoopMetric}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WHOOP_METRICS.map(metric => (
                    <SelectItem key={metric.id} value={metric.id}>
                      <div className="flex items-center gap-2">
                        <metric.icon className="h-4 w-4" />
                        {metric.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* App Metric Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Brain className="h-4 w-4 text-accent" />
                App Metric
              </label>
              <Select value={selectedAppMetric} onValueChange={setSelectedAppMetric}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APP_METRICS.map(metric => (
                    <SelectItem key={metric.id} value={metric.id}>
                      <div className="flex items-center gap-2">
                        <metric.icon className="h-4 w-4" />
                        {metric.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Date Range
              </label>
              <Select value={dateRange.toString()} onValueChange={(v) => setDateRange(parseInt(v) as DateRange)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGES.map(range => (
                    <SelectItem key={range.value} value={range.value.toString()}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Correlation Summary - Available to ALL users */}
      <Card className="border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            Pattern Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summariesLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : relevantCorrelation ? (
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {formatMetricNameForDisplay(relevantCorrelation.metric_a)} ↔ {formatMetricNameForDisplay(relevantCorrelation.metric_b)}
                </span>
                <Badge variant={relevantCorrelation.correlation_strength > 0 ? 'default' : 'secondary'}>
                  {getCorrelationCategoryLabel(relevantCorrelation.correlation_strength)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {relevantCorrelation.insight_text}
              </p>
              <p className="text-xs text-muted-foreground/70">
                Based on {relevantCorrelation.sample_size} data points
              </p>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
              <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-muted-foreground">
                  There is no strong pattern detected in this period.
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Continue tracking consistently to discover meaningful relationships.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts */}
      <Tabs defaultValue="timeline" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="timeline">Time Series</TabsTrigger>
          <TabsTrigger value="scatter">Scatter Plot</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {whoopMetricConfig?.label} vs {appMetricConfig?.label} Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              {combinedData.length < 3 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Need at least 3 days of data to display chart
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={combinedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="displayDate" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    {whoopMetricConfig && (
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey={whoopMetricConfig.dataKey}
                        name={whoopMetricConfig.label}
                        stroke={whoopMetricConfig.color}
                        strokeWidth={2}
                        dot={{ fill: whoopMetricConfig.color, r: 3 }}
                      />
                    )}
                    {appMetricConfig && (
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey={appMetricConfig.dataKey}
                        name={appMetricConfig.label}
                        stroke={appMetricConfig.color}
                        strokeWidth={2}
                        dot={{ fill: appMetricConfig.color, r: 3 }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scatter" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {whoopMetricConfig?.label} vs {appMetricConfig?.label}
              </CardTitle>
              <CardDescription className="text-xs">
                Each point represents one day of data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {scatterData.length < 3 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Need at least 3 days with both metrics to display scatter plot
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name={whoopMetricConfig?.label}
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name={appMetricConfig?.label}
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                    />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value: any, name: string) => [value, name === 'x' ? whoopMetricConfig?.label : appMetricConfig?.label]}
                    />
                    <Scatter 
                      data={scatterData} 
                      fill="hsl(var(--primary))"
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default WhoopCompareView;
